import { useEffect, useMemo, useRef } from 'react';
import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';
import { Grid, Flex, FlexItem, Link } from '@gusto/workbench';
import {
  Form,
  SubmitButton,
  TextField,
  SelectField,
  Option,
  Checkbox,
  CheckboxGroup,
} from '@gusto/workbench-formik';
import { postJson } from 'requests/http';
import { trackError } from 'helpers/track_error';
import FormattedPhoneField from 'packages/signup/components/signupForm/formattedPhoneField';
import numberCompaniesBucket from './companies_bucket_values';
import getSHA256Hash from './partner_form_details_helpers';
import type { AccountantLeadFormDetails } from './types';

const SSO_DETECT_DEBOUNCE_MS = 250;
const SSO_BRIDGE_PATH = '/invite/accountant/start_sso_experience';

const domainCandidates = (domain: string): string[] => {
  const parts = domain.split('.');
  if (parts.length <= 2) return [domain];
  return parts.slice(0, -1).map((_, i) => parts.slice(i).join('.'));
};

const useSsoDetectRedirect = (ssoDomains: string[]) => {
  const { values } = useFormikContext<AccountantLeadFormDetails>();
  const { email, first_name: firstName, last_name: lastName } = values.accountant_lead;
  const redirectingRef = useRef(false);

  const check = useMemo(
    () =>
      debounce(async (currentEmail: string, fname: string, lname: string) => {
        if (redirectingRef.current || !ssoDomains.length || !currentEmail.includes('@')) return;

        const domain = currentEmail.substring(currentEmail.indexOf('@') + 1).toLowerCase();
        const hashes = await Promise.all(domainCandidates(domain).map(getSHA256Hash));
        if (redirectingRef.current || !hashes.some(h => ssoDomains.includes(h))) return;

        redirectingRef.current = true;
        try {
          const res = await postJson(SSO_BRIDGE_PATH, {
            email: currentEmail,
            first_name: fname,
            last_name: lname,
          });
          if (res?.redirect_url) {
            window.location.href = res.redirect_url;
            return;
          }
        } catch {
          trackError({ error: new Error('Partner SSO detect bridge failed') });
        }
        redirectingRef.current = false;
      }, SSO_DETECT_DEBOUNCE_MS),
    [ssoDomains]
  );

  useEffect(() => {
    if (email) check(email, firstName, lastName);
    return () => check.cancel();
  }, [check, email, firstName, lastName]);
};

const PartnerFormDetails = ({ ssoDomains = [] }: { ssoDomains?: string[] }) => {
  useSsoDetectRedirect(ssoDomains);

  return (
    <Grid component={Form} marginBottom={8} rowGap={3}>
      <Flex columnGap={3} rowGap={3} flexDirection="column" flexDirectionMd="row">
        <FlexItem width={1} widthMd={1 / 2}>
          <TextField
            name="accountant_lead.first_name"
            type="text"
            label="First name"
            autoComplete="given-name"
            required
          />
        </FlexItem>
        <FlexItem width={1} widthMd={1 / 2}>
          <TextField
            name="accountant_lead.last_name"
            type="text"
            label="Last name"
            autoComplete="family-name"
            required
          />
        </FlexItem>
      </Flex>
      <TextField
        name="accountant_lead.email"
        type="email"
        label="Business email address"
        autoComplete="email"
        required
      />
      <TextField
        name="accountant_lead.accounting_firm_name"
        type="text"
        label="Firm name"
        required
      />
      <SelectField
        name="accountant_lead.number_companies_bucket"
        label="No. of business clients"
        required
      >
        <Option value="" />
        {numberCompaniesBucket.map(opt => (
          <Option key={opt.label} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </SelectField>
      <SelectField
        name="accountant_lead.number_payroll_services_clients_bucket"
        label="No. of payroll services clients"
        required
      >
        <Option value="" />
        {numberCompaniesBucket.map(opt => (
          <Option key={opt.label} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </SelectField>
      <TextField name="accountant_lead.website" type="text" label="Website" />
      <FormattedPhoneField
        name="accountant_lead.phone"
        label="Phone number"
        type="tel"
        placeholder="(555) 012-3456"
        maxLength={14}
        autoComplete="tel"
        required
      />
      <CheckboxGroup legend=" " name="terms_acceptance">
        <Checkbox
          label={
            <>
              I accept the{' '}
              <Link href="https://gusto.com/legal/partners" target="_blank">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link href="https://gusto.com/legal/privacy" target="_blank">
                Privacy Policy
              </Link>
            </>
          }
        />
      </CheckboxGroup>
      <SubmitButton className="btn btn-submit-form">Sign up with email</SubmitButton>
    </Grid>
  );
};

export default PartnerFormDetails;
